import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/Slices/busSlice';
import logo from '../../components/assets/logos/logo2.PNG';
import { FaHome } from 'react-icons/fa';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentLanguage = useSelector(selectLanguage);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant' 
    });
  }, []); 

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    htmlElement.lang = currentLanguage;
    htmlElement.classList.remove('hindi-lang');
    bodyElement.classList.remove('hindi-lang');
    
    if (currentLanguage === 'hi') {
      htmlElement.classList.add('hindi-lang');
      bodyElement.classList.add('hindi-lang');
    }
    
    htmlElement.classList.add('font-transition');
    bodyElement.classList.add('font-transition');
    
    return () => {
      htmlElement.classList.remove('hindi-lang', 'font-transition');
      bodyElement.classList.remove('hindi-lang', 'font-transition');
    };
  }, [currentLanguage]);

  const getFontConfig = () => {
    if (currentLanguage === 'hi') {
      return {
     fontFamily: "'Noto Serif Devanagari', serif",


        fontWeight: 500,
        className: 'hindi-lang font-hindi'
      };
    }
    return {
     fontFamily: "'Noto Serif Devanagari', serif",


      fontWeight: 400,
      className: 'font-english'
    };
  };

  const fontConfig = getFontConfig();

  const handleBack = () => {
    navigate('/');
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: fontConfig.fontFamily,
      fontWeight: fontConfig.fontWeight,
      padding: isMobile ? '10px' : '0',
      overflowX: 'hidden',
      transition: 'font-family 0.3s ease, font-weight 0.3s ease'
    },
    header: {
      background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%)',
      color: 'black',
      padding: isMobile ? '10px 20px' : '10px 40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      borderBottom: '3px solid #ffeb3b'
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      textDecoration: 'none',
      color: 'black'
    },
    logoImage: {
      height: isMobile ? '45px' : '60px',
      width: 'auto',
      objectFit: 'contain',
      borderRadius: '8px'
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: 'black',
      fontSize: isMobile ? '1rem' : '1.1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      padding: '8px 15px',
      borderRadius: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      transition: 'all 0.3s ease'
    },
    mainContent: {
      flex: '1',
      padding: isMobile ? '20px' : '40px',
      maxWidth: '900px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box'
    },
    pageCard: {
      background: 'white',
      borderRadius: '20px',
      padding: isMobile ? '25px' : '40px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      marginBottom: '30px'
    },
    pageTitle: {
      color: '#333',
      fontSize: isMobile ? '2rem' : '2.5rem',
      marginBottom: '30px',
      textAlign: 'center',
      fontWeight: 700,
      borderBottom: '3px solid #ffeb3b',
      paddingBottom: '15px'
    },
    section: {
      marginBottom: '30px'
    },
    sectionTitle: {
      color: '#444',
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      marginBottom: '15px',
      fontWeight: 600
    },
    paragraph: {
      color: '#666',
      fontSize: isMobile ? '0.95rem' : '1rem',
      lineHeight: '1.8',
      marginBottom: '15px',
      textAlign: 'justify'
    },
    list: {
      paddingLeft: '20px',
      marginBottom: '15px'
    },
    listItem: {
      color: '#666',
      fontSize: isMobile ? '0.95rem' : '1rem',
      lineHeight: '1.8',
      marginBottom: '10px'
    },
    lastUpdated: {
      marginTop: '30px',
      padding: '15px',
      background: '#f8f9fa',
      borderRadius: '10px',
      color: '#999',
      fontSize: isMobile ? '0.85rem' : '0.9rem',
      textAlign: 'center',
      fontStyle: 'italic'
    },
    footer: {
      background: '#fff9c4',
      color: 'black',
      padding: isMobile ? '15px 20px' : '25px 40px',
      textAlign: 'center',
      marginTop: '40px',
      width: '100%',
      boxSizing: 'border-box'
    },
    footerText: {
      margin: '0 0 10px 0',
      opacity: '0.9',
      fontSize: isMobile ? '0.8rem' : '0.95rem'
    },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: isMobile ? '15px' : '25px'
    },
    footerLink: {
      color: 'black',
      textDecoration: 'none',
      opacity: '0.8',
      transition: 'opacity 0.3s ease',
      fontSize: isMobile ? '0.8rem' : '0.9rem'
    }
  };

  return (
    <div style={styles.container} className={fontConfig.className} lang={currentLanguage}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoContainer}>
            <Link to="/" style={styles.logoLink}>
              <img src={logo} alt="Logo" style={styles.logoImage} />
            </Link>
          </div>
          <button 
            onClick={handleBack} 
            style={styles.backButton}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
          >
            <FaHome /> {t('home', 'Home')}
          </button>
        </div>
      </header>

      <main style={styles.mainContent}>
        <div style={styles.pageCard}>
          <h1 style={styles.pageTitle}>
            {t('privacyPolicy', 'Privacy Policy')}
          </h1>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('introduction', 'Introduction')}
            </h2>
            <p style={styles.paragraph}>
              {t('privacyIntro', 
                'Rajdoot Explorations ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our bus management services.')}
            </p>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('infoCollection', 'Information We Collect')}
            </h2>
            <p style={styles.paragraph}>
              {t('infoCollectionText', 'We may collect personal information that you voluntarily provide to us when you register on the Service, express interest in obtaining information about us or our services, or otherwise contact us. The personal information we collect may include:')}
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>• {t('nameInfo', 'Name and contact information (email address, phone number)')}</li>
              <li style={styles.listItem}>• {t('busInfo', 'Bus registration details and operator information')}</li>
              <li style={styles.listItem}>• {t('locationInfo', 'Location data and route preferences')}</li>
              <li style={styles.listItem}>• {t('deviceInfo', 'Device information and usage data')}</li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('infoUsage', 'How We Use Your Information')}
            </h2>
            <p style={styles.paragraph}>
              {t('usageText', 'We use the information we collect to:')}
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>• {t('provideServices', 'Provide, operate, and maintain our bus management services')}</li>
              <li style={styles.listItem}>• {t('improveServices', 'Improve, personalize, and expand our services')}</li>
              <li style={styles.listItem}>• {t('communicate', 'Communicate with you about updates, services, and promotions')}</li>
              <li style={styles.listItem}>• {t('security', 'Ensure the security and integrity of our platform')}</li>
              <li style={styles.listItem}>• {t('analytics', 'Analyze usage patterns and optimize user experience')}</li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('dataProtection', 'Data Protection')}
            </h2>
            <p style={styles.paragraph}>
              {t('dataProtectionText', 'We implement appropriate technical and organizational security measures to protect your personal information. However, please note that no method of transmission over the Internet or electronic storage is 100% secure.')}
            </p>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('thirdParty', 'Third-Party Services')}
            </h2>
            <p style={styles.paragraph}>
              {t('thirdPartyText', 'We may employ third-party companies and individuals to facilitate our Service, provide service-related services, or assist us in analyzing how our Service is used. These third parties have access to your personal information only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.')}
            </p>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('cookies', 'Cookies and Tracking Technologies')}
            </h2>
            <p style={styles.paragraph}>
              {t('cookiesText', 'We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.')}
            </p>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('policyChanges', 'Changes to This Privacy Policy')}
            </h2>
            <p style={styles.paragraph}>
              {t('policyChangesText', 'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.')}
            </p>
          </div>

          <div style={styles.lastUpdated}>
            {t('lastUpdated', 'Last Updated')}: {new Date().toLocaleDateString()}
          </div>
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          © {new Date().getFullYear()} {t('companyName', 'Rajdoot Explorations')}. {t('allRightsReserved', 'All rights reserved.')}
        </p>
        <div style={styles.footerLinks}>
          <Link to="/privacy" style={styles.footerLink}>{t('privacyPolicy', 'Privacy Policy')}</Link>
          <Link to="/terms" style={styles.footerLink}>{t('termsOfService', 'Terms of Service')}</Link>
          <Link to="/contact" style={styles.footerLink}>{t('contactUs', 'Contact Us')}</Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;